import find from 'lodash/find';
import type { SingleValue } from 'react-select';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { useCheckOnFocus } from '@hooks/useCheckOnFocus';

import { createDefaultOption, resolvePlaceholder } from '../../utils/selectHelpers';
import type { DefaultOption } from '../../constants/constants';
import type { OptionType } from '../../Select';

interface Params {
    defaultOptions: OptionType[] | boolean | undefined;
    defaultOptionType: DefaultOption | undefined;
    selectedValue: Nullable<string | number> | undefined;
    value: OptionType | undefined;
    placeholder: string | undefined;
    title: string | undefined;
    forcePlaceholder: boolean | undefined;
    name: string | undefined;
    onChange(params: Nullable<OptionType>, name?: string): void;
}

interface Data {
    isOnFocus: boolean;
    value: Nullable<OptionType>;
    defaultOptions: OptionType[] | boolean;
    placeholder: string | undefined;
    handleChange(selectedItem: SingleValue<OptionType>): void;
    handleFocus(): void;
    handleBlur(): void;
}

export function useAsyncSelect({
    defaultOptions,
    defaultOptionType,
    selectedValue,
    value,
    placeholder,
    title,
    forcePlaceholder,
    name,
    onChange,
}: Params): Data {
    const { isOnFocus, handleFocus, handleBlur } = useCheckOnFocus();

    const resolvedDefaultOptions = buildDefaultOptions(defaultOptions, defaultOptionType);
    const resolvedValue = selectedValue
        ? (find(resolvedDefaultOptions as OptionType[], option => option.value === selectedValue) ?? null)
        : (value ?? null);

    return {
        isOnFocus,
        value: resolvedValue,
        defaultOptions: resolvedDefaultOptions,
        placeholder: resolvePlaceholder({ forcePlaceholder, placeholder, title, isOnFocus }),
        handleChange: selectedItem => onChange(selectedItem, name),
        handleFocus,
        handleBlur,
    };
}

function buildDefaultOptions(
    defaultOptions: OptionType[] | boolean | undefined,
    defaultOptionType: DefaultOption | undefined,
): OptionType[] | boolean {
    if (typeof defaultOptions === 'boolean') {
        return defaultOptions;
    }

    const result: OptionType[] = [];

    if (defaultOptions) {
        result.push(...defaultOptions);
    }
    if (defaultOptionType) {
        result.push(createDefaultOption(defaultOptionType));
    }

    return result;
}
